import 'core-js/modules/es.array.concat.js';
import 'core-js/modules/es.object.assign.js';
import { __rest } from 'tslib';
import { jsxs, jsx } from 'react/jsx-runtime';
import React, { useEffect } from 'react';
import Collapse from '@mui/material/Collapse';
import classNames from 'classnames';
import { getThemeClassNames } from '@cognite/cogs-utils';
import AccordionHeader from './accordion-header.js';

/**
 * Accordion
 * =============================================================================
 * An accordion is a vertically stacked list of headers that reveal or hide associated sections of content.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-accordion--docs)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Accordion
 *  title="Which browsers can I use?"
 *  subtitle="Last updated 6 hours ago"
 * >
 *   You can use the latest versions of all major browsers.
 * </Accordion>
 *
 * ```
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-accordion--docs)
 */
var Accordion = /*#__PURE__*/React.forwardRef(function (_a, ref) {
  var className = _a.className,
    children = _a.children,
    title = _a.title,
    subtitle = _a.subtitle,
    _a$size = _a.size,
    size = _a$size === undefined ? 'medium' : _a$size,
    _a$type = _a.type,
    type = _a$type === undefined ? 'solid' : _a$type,
    inverted = _a.inverted,
    _a$disabled = _a.disabled,
    disabled = _a$disabled === undefined ? false : _a$disabled,
    _a$expanded = _a.expanded,
    expanded = _a$expanded === undefined ? false : _a$expanded,
    _a$showBorder = _a.showBorder,
    showBorder = _a$showBorder === undefined ? false : _a$showBorder,
    _a$indicatorPlacement = _a.indicatorPlacement,
    indicatorPlacement = _a$indicatorPlacement === undefined ? 'right' : _a$indicatorPlacement,
    _a$hidePadding = _a.hidePadding,
    hidePadding = _a$hidePadding === undefined ? false : _a$hidePadding,
    onChange = _a.onChange,
    _a$id = _a.id,
    id = _a$id === undefined ? undefined : _a$id,
    themeProps = _a.themeProps,
    otherProps = __rest(_a, ["className", "children", "title", "subtitle", "size", "type", "inverted", "disabled", "expanded", "showBorder", "indicatorPlacement", "hidePadding", "onChange", "id", "themeProps"]);
  var _React$useState = React.useState(expanded),
    _expanded = _React$useState[0],
    setExpanded = _React$useState[1];
  useEffect(function () {
    setExpanded(expanded);
  }, [expanded]);
  var expand = function expand() {
    setExpanded(!_expanded);
    if (onChange) onChange(!_expanded);
  };
  return jsxs("div", Object.assign({}, otherProps, {
    className: classNames.apply(undefined, ['cogs-v10', 'cogs-accordion', "cogs-accordion--size-" + size, "cogs-accordion--type-" + type, _expanded ? "cogs-accordion--expanded" : '', disabled ? "cogs-accordion--disabled" : '', showBorder ? "cogs-accordion--showBorder" : '', hidePadding ? "cogs-accordion--hidePadding" : ''].concat(getThemeClassNames(inverted, themeProps), [className])),
    ref: ref,
    "aria-disabled": disabled,
    id: id,
    children: [jsx(AccordionHeader, {
      disabled: disabled,
      expand: expand,
      title: title,
      expanded: _expanded,
      subtitle: subtitle,
      indicatorPlacement: indicatorPlacement
    }), jsx(Collapse, {
      "in": _expanded,
      timeout: "auto",
      unmountOnExit: true,
      children: jsx("div", {
        className: "cogs-v10 cogs-accordion__content",
        children: children
      })
    })]
  }));
});

export { Accordion };
